import React, { useMemo, useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  CheckSquare,
  Square,
  ExternalLink,
  Eye,
  Target,
  Users
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PartnerCampaign } from '../types';
import { getCountryNames } from '../data/countries';
import { ConfirmationModal } from './ConfirmationModal';
import { NotificationModal } from './NotificationModal';

export const PartnerCampaignManager: React.FC = () => {
  const {
    user,
    advertisements,
    partnerCampaigns,
    marketingCampaigns,
    promoCodeRequests,
    partners,
    createPartnerCampaign, 
    updatePartnerCampaign, 
    deletePartnerCampaign 
  } = useAuth();
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<PartnerCampaign | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Modal states
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'warning' | 'danger' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning'
  });
  
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type?: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    priority: 1,
    status: 'draft' as PartnerCampaign['status'],
    advertisement_ids: [] as string[],
    region: '' // Selected region for the campaign
  });

  const resetCampaignForm = () => {
    setCampaignForm({
      name: '',
      description: '',
      priority: 1,
      status: 'draft',
      advertisement_ids: [],
      region: ''
    });
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.partner_id) return;
    
    if (campaignForm.status === 'active' && !canCreateActiveCampaignInRegion(campaignForm.region)) {
      const conflictingCampaign = getActiveCampaignInRegion(campaignForm.region);
      setNotification({
        isOpen: true,
        title: 'Region Conflict Warning',
        message: conflictingCampaign
          ? `You already have an active campaign "${conflictingCampaign.name}" in ${getCountryNames([campaignForm.region])[0]} region. Please set the existing campaign to Draft/Completed before creating another active campaign.`
          : 'You already have an active campaign in this region. Please set it to Draft/Completed before creating another active campaign.',
        type: 'warning'
      });
      return;
    }
    
    try {
      await createPartnerCampaign({
        ...campaignForm,
        partner_id: user.partner_id,
        created_by: user.id
      });
      setShowCreateModal(false);
      resetCampaignForm();
      setNotification({
        isOpen: true,
        title: 'Success',
        message: 'Campaign created successfully!',
        type: 'success'
      });
    } catch (error) {
      setNotification({
        isOpen: true,
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create campaign',
        type: 'error'
      });
    }
  };

  const handleUpdateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCampaign) {
      if (campaignForm.status === 'active' && !canCreateActiveCampaignInRegion(campaignForm.region, editingCampaign.id)) {
        const conflictingCampaign = getActiveCampaignInRegion(campaignForm.region);
        setNotification({
          isOpen: true,
          title: 'Region Conflict Warning',
          message: conflictingCampaign
            ? `You already have an active campaign "${conflictingCampaign.name}" in ${getCountryNames([campaignForm.region])[0]} region. Please set the existing campaign to Draft/Completed before activating another campaign.`
            : 'You already have an active campaign in this region. Please set it to Draft/Completed before activating another campaign.',
          type: 'warning'
        });
        return;
      }
      try {
        await updatePartnerCampaign(editingCampaign.id, campaignForm);
        setEditingCampaign(null);
        resetCampaignForm();
        setNotification({
          isOpen: true,
          title: 'Success',
          message: 'Campaign updated successfully!',
          type: 'success'
        });
      } catch (error) {
        setNotification({
          isOpen: true,
          title: 'Error',
          message: error instanceof Error ? error.message : 'Failed to update campaign',
          type: 'error'
        });
      }
    }
  };

  const startEditCampaign = (campaign: PartnerCampaign) => {
    setEditingCampaign(campaign);
    setCampaignForm({
      name: campaign.name,
      description: campaign.description,
      priority: campaign.priority,
      status: campaign.status,
      advertisement_ids: [...campaign.advertisement_ids],
      region: campaign.region || ''
    });
  };

  const toggleAdvertisement = (adId: string) => {
    setCampaignForm(prev => ({
      ...prev,
      advertisement_ids: prev.advertisement_ids.includes(adId)
        ? prev.advertisement_ids.filter(id => id !== adId)
        : [...prev.advertisement_ids, adId]
    }));
  };

  const partnerCampaignsFiltered = partnerCampaigns.filter(campaign => 
    campaign.partner_id === user?.partner_id &&
    campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (statusFilter === 'all' || campaign.status === statusFilter)
  );

  // Get current partner's regions
  const currentPartner = partners.find(p => p.id === user?.partner_id);
  const partnerRegions = currentPartner?.region || [];

  // Check if partner can create active campaign in a specific region
  const canCreateActiveCampaignInRegion = (region: string, excludeCampaignId?: string) => {
    if (!user?.partner_id || !region) return true;
    
    const existingActiveCampaignsInRegion = partnerCampaigns.filter(pc => 
      pc.partner_id === user.partner_id && 
      pc.status === 'active' && 
      pc.region === region &&
      pc.id !== excludeCampaignId
    );
    
    return existingActiveCampaignsInRegion.length === 0;
  };

  // Get existing active campaign in region (if any)
  const getActiveCampaignInRegion = (region: string) => {
    if (!user?.partner_id || !region) return null;
    
    return partnerCampaigns.find(pc => 
      pc.partner_id === user.partner_id && 
      pc.status === 'active' && 
      pc.region === region
    );
  };

  const partnerAds = advertisements.filter(ad => ad.partner_id === user?.partner_id);

  const partnerPromoRequests = useMemo(() => (
    promoCodeRequests.filter(req => req.partner_id === user?.partner_id)
  ), [promoCodeRequests, user?.partner_id]);

  const campaignPromoRequestCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    const validCampaignIds = new Set(
      partnerCampaigns
        .filter(pc => pc.partner_id === user?.partner_id)
        .map(pc => pc.id)
    );

    const adToCampaignIds: Record<string, string[]> = {};
    partnerCampaigns.forEach(campaign => {
      if (campaign.partner_id !== user?.partner_id) return;
      campaign.advertisement_ids.forEach(adId => {
        if (!adToCampaignIds[adId]) {
          adToCampaignIds[adId] = [];
        }
        adToCampaignIds[adId].push(campaign.id);
      });
    });

    const incrementCount = (id: string) => {
      counts[id] = (counts[id] || 0) + 1;
    };

    partnerPromoRequests.forEach(req => {
      if (req.partner_campaign_id && validCampaignIds.has(req.partner_campaign_id)) {
        incrementCount(req.partner_campaign_id);
        return;
      }

      if (req.advertisement_id && adToCampaignIds[req.advertisement_id]) {
        adToCampaignIds[req.advertisement_id].forEach(incrementCount);
      }
    });

    return counts;
  }, [partnerCampaigns, partnerPromoRequests, user?.partner_id]);
  
  // Filter advertisements by selected campaign region
  const availableAdsForCampaign = campaignForm.region 
    ? partnerAds.filter(ad => ad.region === campaignForm.region)
    : partnerAds;
  // Filter and sort available advertisements for campaign
  const availableAdsForCampaignFiltered = availableAdsForCampaign.filter(ad => 
    ad.status === 'active' && 
    ad.approval_status === 'approved' &&
    new Date(ad.end_date) >= new Date() &&
    ad.advertisement_mode !== 'independent' // Exclude independent ads - they're only for direct links
  ).sort((a, b) => {
    // Sort by priority: platinum > gold > silver > bronze
    const priorityOrder = { platinum: 1, gold: 2, silver: 3, bronze: 4 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
  }> = ({ title, value, icon, color }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition duration-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Campaign Manager</h2>
        <p className="text-gray-600 mt-2">Create and manage your partner campaigns</p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Campaigns"
          value={partnerCampaignsFiltered.length}
          icon={<Target className="text-white" size={24} />}
          color="bg-purple-500"
        />
        <MetricCard
          title="Active Campaigns"
          value={partnerCampaignsFiltered.filter(c => c.status === 'active').length}
          icon={<Eye className="text-white" size={24} />}
          color="bg-green-500"
        />
        <MetricCard
          title="Promo Requests"
          value={partnerPromoRequests.length}
          icon={<Users className="text-white" size={24} />}
          color="bg-blue-500"
        />
        <MetricCard
          title="Available Ads"
          value={partnerAds.length}
          icon={<Users className="text-white" size={24} />}
          color="bg-orange-500"
        />
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-purple-600 hover:to-pink-700 transition duration-200 flex items-center"
        >
          <Plus size={20} className="mr-2" />
          New Campaign
        </button>
      </div>

      {/* Campaigns Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Advertisements
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Promo Requests
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {partnerCampaignsFiltered.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                      <div className="text-sm text-gray-500">{campaign.description}</div>
                      {campaign.region && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                            🌍 {getCountryNames([campaign.region])[0]} ({campaign.region})
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(campaign.status)}`}>
                      {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="flex flex-col gap-1 items-start">
                      {campaign.advertisement_ids.map(adId => {
                        const ad = advertisements.find(a => a.id === adId);
                        return ad ? (
                          <span key={adId} className="inline-flex px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {ad.name}
                          </span>
                        ) : null;
                      })}
                      {campaign.advertisement_ids.length === 0 && (
                        <span className="text-xs text-gray-400 italic">No advertisements</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {campaign.status === 'active' ? (campaignPromoRequestCounts[campaign.id] ?? 0) : '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {campaign.created_at 
                      ? new Date(campaign.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })
                      : 'N/A'
                    }
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          // Open preview in new tab with partner and campaign filters
                          const previewUrl = `/partner-campaign-preview?partner=${user?.partner_id}&campaign=${campaign.id}`;
                          console.log('Opening partner campaign preview:', previewUrl);
                          window.open(previewUrl, '_blank');
                        }}
                        className="text-green-600 hover:text-green-800 transition duration-200"
                        title="Preview Campaign"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => startEditCampaign(campaign)}
                        className="text-blue-600 hover:text-blue-800 transition duration-200"
                        title="Edit Campaign"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => {
                          setConfirmModal({
                            isOpen: true,
                            title: 'Delete Campaign',
                            message: `Are you sure you want to delete "${campaign.name}"? This action cannot be undone.`,
                            type: 'danger',
                            onConfirm: async () => {
                              try {
                                await deletePartnerCampaign(campaign.id);
                                setNotification({
                                  isOpen: true,
                                  title: 'Success',
                                  message: 'Campaign deleted successfully!',
                                  type: 'success'
                                });
                              } catch (error) {
                                setNotification({
                                  isOpen: true,
                                  title: 'Error',
                                  message: 'Failed to delete campaign: ' + (error instanceof Error ? error.message : 'Unknown error'),
                                  type: 'error'
                                });
                              }
                            }
                          });
                        }}
                        className="text-red-600 hover:text-red-800 transition duration-200"
                        title="Delete Campaign"
                      >
                        <Trash2 size={16} />
                      </button>
                      {/* Public Link Button - only show if campaign is active and included in a marketing campaign */}
                      {campaign.status === 'active' && (
                        (() => {
                          // Check if this partner campaign is included in any approved marketing campaign
                          const isIncludedInApprovedCampaign = marketingCampaigns.some(mc => 
                            mc.status === 'active' && mc.partner_campaign_ids.includes(campaign.id)
                          );
                          
                          return (
                            <button
                              onClick={() => {
                                if (isIncludedInApprovedCampaign) {
                                  const link = `${window.location.origin}/public-campaigns?partner=${campaign.partner_id}&partnerCampaign=${campaign.id}`;
                                  navigator.clipboard.writeText(link);
                                  alert('Public campaign link copied to clipboard!');
                                }
                              }}
                              className={`transition duration-200 ${
                                isIncludedInApprovedCampaign 
                                  ? 'text-green-600 hover:text-green-800 cursor-pointer' 
                                  : 'text-gray-400 cursor-not-allowed'
                              }`}
                              title={isIncludedInApprovedCampaign ? 'Copy Public Link' : 'Campaign must be approved in global campaign first'}
                              disabled={!isIncludedInApprovedCampaign}
                            >
                              <ExternalLink size={16} />
                            </button>
                          );
                        })()
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Campaign Modal */}
      {(showCreateModal || editingCampaign) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingCampaign ? 'Edit Partner Campaign' : 'Create New Partner Campaign'}
            </h3>
            <form onSubmit={editingCampaign ? handleUpdateCampaign : handleCreateCampaign} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name</label>
                  <input
                    type="text"
                    value={campaignForm.name}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter campaign name"
                    required
                  />
                </div>

                {/* Region Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Region <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={campaignForm.region}
                    onChange={(e) => {
                      setCampaignForm(prev => ({ 
                        ...prev, 
                        region: e.target.value,
                        advertisement_ids: [] // Clear selected ads when region changes
                      }));
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a region</option>
                    {partnerRegions.map((regionCode) => (
                      <option key={regionCode} value={regionCode}>
                        {getCountryNames([regionCode])[0]} ({regionCode})
                      </option>
                    ))}
                  </select>
                  {partnerRegions.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">
                      No regions assigned to your partner. Contact admin to assign regions.
                    </p>
                  )}
                  {partnerRegions.length > 1 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Only advertisements from the selected region will be available.
                    </p>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Enter campaign description"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={campaignForm.status}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, status: e.target.value as PartnerCampaign['status'] }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="completed">Completed</option>
                </select>
                
                {/* Warning for active status when region already has active campaign */}
                {campaignForm.status === 'active' && campaignForm.region && !canCreateActiveCampaignInRegion(campaignForm.region) && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start">
                      <div className="text-amber-600 mr-2">⚠️</div>
                      <div>
                        <p className="text-sm font-medium text-amber-800">
                          Region Conflict Warning
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          You already have an active campaign "{getActiveCampaignInRegion(campaignForm.region)?.name}" in {getCountryNames([campaignForm.region])[0]} region. 
                          Only one active campaign per region is allowed. The existing campaign will need to be set to draft or completed status first.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Select Advertisements ({campaignForm.advertisement_ids.length} selected)
                </label>
                
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                  {availableAdsForCampaignFiltered.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      {campaignForm.region 
                        ? `No active advertisements available for ${getCountryNames([campaignForm.region])[0]}. Create active advertisements for this region first.`
                        : 'No active advertisements available. Create active advertisements first.'
                      }
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {availableAdsForCampaignFiltered.map((ad: any) => {
                        const isSelected = campaignForm.advertisement_ids.includes(ad.id);
                        
                        return (
                          <div key={ad.id} className="p-4 hover:bg-gray-50">
                            <div className="flex items-start space-x-3">
                              <button
                                type="button"
                                onClick={() => toggleAdvertisement(ad.id)}
                                className="mt-1 text-blue-600 hover:text-blue-800"
                              >
                                {isSelected ? (
                                  <CheckSquare size={20} />
                                ) : (
                                  <Square size={20} />
                                )}
                              </button>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-sm font-medium text-gray-900">
                                    {ad.name}
                                  </h4>
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    ad.priority === 'platinum' ? 'bg-purple-100 text-purple-800' :
                                    ad.priority === 'gold' ? 'bg-yellow-100 text-yellow-800' :
                                    ad.priority === 'silver' ? 'bg-gray-200 text-gray-800' :
                                    'bg-orange-100 text-orange-800'
                                  }`}>
                                    {ad.priority.charAt(0).toUpperCase() + ad.priority.slice(1)}
                                  </span>
                                </div>
                                <p className="text-sm mt-1 text-gray-600">
                                  {ad.description}
                                </p>
                                  <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                                    <span>{new Date(ad.start_date).toLocaleDateString()} - {new Date(ad.end_date).toLocaleDateString()}</span>
                                    <span className={`px-2 py-1 rounded-full ${getStatusColor(ad.status)}`}>
                                      {ad.status}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex-shrink-0">
                                  <img
                                    src={ad.banner_url}
                                    alt={ad.name}
                                    className="w-16 h-10 object-cover rounded"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.src = 'https://images.pexels.com/photos/1267338/pexels-photo-1267338.jpeg?auto=compress&cs=tinysrgb&w=800';
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingCampaign(null);
                    resetCampaignForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg hover:from-purple-600 hover:to-pink-700 transition duration-200"
                >
                  {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Modals */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <NotificationModal
        isOpen={notification.isOpen}
        onClose={() => setNotification(prev => ({ ...prev, isOpen: false }))}
        title={notification.title}
        message={notification.message}
        type={notification.type}
        autoClose={notification.type === 'success'}
        autoCloseDelay={3000}
      />
    </div>
  );
};